/**
 * FORGOT PASSWORD COMPONENT
 * =========================
 * 
 * Handles password reset requests for users who have forgotten their passwords.
 * Provides a simple form to enter email address and sends reset instructions.
 * Now features the custom brand logo for consistent branding.
 * 
 * FEATURES:
 * - Email validation and form handling
 * - Success state with instructions
 * - Navigation back to sign-in
 * - Custom brand logo integration
 * - Responsive design
 * - Loading states and error handling
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { ArrowLeft, AlertCircle, CheckCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "@/hooks/useAuth";

interface ForgotPasswordProps {
  onBackToSignIn: () => void;
  onBackToLanding: () => void;
}

export function ForgotPassword({ onBackToSignIn, onBackToLanding }: ForgotPasswordProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    if (!email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await resetPassword(email);
      
      if (error) {
        setError(error.message);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setIsSuccess(true);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <div className="border-b">
          <div className="container flex items-center h-16">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToLanding}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Logo 
              size="md" 
              onClick={onBackToLanding}
              showText={true}
            />
          </div>
        </div>

        {/* Success Content */}
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="w-full max-w-md space-y-6">
            <Card>
              <CardContent className="pt-6 text-center space-y-4">
                <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Check your email</h3>
                  <p className="text-sm text-muted-foreground">
                    We've sent a password reset link to <strong>{email}</strong>
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    Didn't receive the email? Check your spam folder or try again. If you still don't find it your account was registered with another email, contact support for help.
                  </p>
                  <div className="space-y-2">
                    <Button
                      onClick={() => setIsSuccess(false)}
                      variant="outline"
                      className="w-full"
                    >
                      Try again
                    </Button>
                    <Button
                      onClick={onBackToSignIn}
                      variant="link"
                      className="w-full"
                    >
                      Back to sign in
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b">
        <div className="container flex items-center h-16">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToLanding}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Logo 
            size="md" 
            onClick={onBackToLanding}
            showText={true}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1>Forgot your password?</h1>
            <p className="text-muted-foreground">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          <Card>
            <CardHeader className="space-y-1">
              <CardTitle>Reset password</CardTitle>
              <CardDescription>
                Enter the email address associated with your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send reset link"}
                </Button>
              </form>

              <div className="text-center">
                <Button
                  variant="link"
                  onClick={onBackToSignIn}
                  disabled={isLoading}
                >
                  Back to sign in
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}